import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  faAnalytics,
  faCog,
  faFlaskGear,
  faPresentation,
  faSliders,
  faTable,
  faTachometer,
  faTools,
} from '@fortawesome/pro-solid-svg-icons';
import { DemandScienceDefault } from '@frontend/theme';
import { NavItemService } from '@frontend/util/nav-item-access';
import { AuthService } from 'auth';
import { EnvService } from 'frontend-environment';
import { PrimeNG } from 'primeng/config';
import { combineLatest, map } from 'rxjs';

@Component({
  selector: 'ds-measure-studio-wrapper',
  template: '<router-outlet></router-outlet>',
  imports: [RouterModule],
  host: {
    '[style.height]': '"100%"',
  },
})
export class MeasureStudioWrapperComponent {
  constructor() {
    inject(PrimeNG).setConfig({
      theme: {
        preset: DemandScienceDefault,
        options: {
          prefix: '',
          darkModeSelector: 'none',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
    });
    const envService = inject(EnvService);
    const authService = inject(AuthService);
    const navItemService = inject(NavItemService);

    navItemService.setAppName('Measurement Studio');
    navItemService.setUser(
      combineLatest([
        authService.actualUserOktaData$,
        authService.hubUserData$,
      ]).pipe(
        map(([oktaData, hubOrgData]) => {
          return {
            fullname: oktaData?.name || 'Admin User',
            email: oktaData?.email || 'admin@terminus.com',
            couName: hubOrgData?.orgName,
          };
        }),
      ),
    );

    navItemService.setNavItems([
      [
        {
          text: 'ABM Scorecard',
          tooltip: 'ABM Scorecard',
          path: 'measure-studio/scorecard',
          icon: faAnalytics,
        },
        {
          text: 'Dashboard',
          tooltip: 'Dashboard',
          path: 'measure-studio/dashboards',
          icon: faTachometer,
        },
        {
          text: 'Engagement Funnel',
          tooltip: 'Engagement Funnel',
          path: 'measure-studio/analyze/engagement-funnel',
          requireHubReportsAccess: 'app.measurementStudio.accountFunnel',
          requireNotLimitedView: false,
          icon: faTachometer,
          iconUrl: '../../../assets/icons/engagement-funnel.svg',
          id: 'account-funnel-measure-studio',
        },
        {
          title: 'Reports',
          tooltip: 'Reports',
          icon: faTable,
          items: [
            // header: Standard
            {
              text: 'Ad Insights',
              path: 'measure-studio/ad-insights',
              requireHubReportsAccess:
                'app.measurementStudio.advertisingInsights',
            },
            {
              text: 'Opportunity Insights',
              path: 'measure-studio/analyze/opportunities/list-analysis',
              requireHubReportsAccess: 'app.analyze.opportunities.listAnalysis',
            },
            // header: Revenue Attribution
            {
              text: 'Marketing Influence',
              path: 'measure-studio/discover/revenue-and-pipeline/marketing-impact',
              requireHubReportsAccess:
                'app.discover.revenueAndPipeline.marketingImpact',
            },
            {
              text: 'Campaign Analytics',
              path: 'measure-studio/analyze/campaigns/list-analysis/campaign-groups',
              requireHubReportsAccess: 'app.analyze.campaigns.listAnalysis',
            },
            {
              text: 'Campaign Trends',
              path: 'measure-studio/analyze/campaigns/trending-analysis',
              requireHubReportsAccess: 'app.analyze.campaigns.trendingAnalysis',
            },
            {
              text: 'Attribution by Campaign Type',
              path: 'measure-studio/discover/revenue-and-pipeline/attribution-by-quarter',
              requireHubReportsAccess:
                'app.discover.revenueAndPipeline.attributionByQuarter',
            },
            // header: Digital Attribution
            {
              text: 'Web Activities List',
              path: 'measure-studio/analyze/digital-attribution/web-activity/channel-group',
              requireHubReportsAccess: 'app.analyze.webTracking.webActivity',
            },
            {
              text: 'Web Activities Trending',
              path: 'measure-studio/analyze/digital-attribution/web-activity-trending',
              requireHubReportsAccess:
                'app.analyze.webTracking.webActivityTrending',
            },
          ],
        },
      ],
      [
        {
          title: 'DemandScience Labs',
          tooltip: 'DemandScience Labs',
          icon: faFlaskGear,
          // gainsightId: 'terminus_labs_ms',
          items: [],
        },
      ],
      [
        {
          title: 'Settings',
          tooltip: 'Settings',
          icon: faCog,
          items: [
            {
              text: 'Visitor Tracking',
              path: '/data-studio/settings/visitor-tracking',
            },
            {
              text: 'Permissions',
              path: '/data-studio/settings/permissions/team',
            },
            {
              text: 'Stages',
              path: '/data-studio/settings/stages',
            },
            {
              text: 'Pages And Features',
              path: '/data-studio/settings/pages-and-features',
            },
            {
              text: 'Data Hygiene',
              path: '/data-studio/settings/data-hygiene/orphan-lead-finder',
            },
            { text: 'Campaign Scoring', path: '/settings/campaign-scoring' },
            {
              text: 'Digital Attribution',
              path: '/data-studio/settings/webTracking',
            },
            {
              text: 'More Settings',
              path: `${envService.LEGACY_STUDIO_APP_URL}/#/settings/self-configure/field-mapping`,
            },
          ],
        },
      ],
      [
        {
          title: 'Admin Tools',
          tooltip: 'Admin Tools',
          icon: faTools,
          requireAdmin: true,
          id: 'admin',
          items: [
            {
              text: 'Switch User',
              tooltip: 'Switch User',
              path: 'measure-studio/admin/switch-user',
            },
            {
              text: 'Admin Settings',
              path: 'measure-studio/admin/settings',
            },
            {
              text: 'Admin Entitlement Toggle',
              path: '',
            },
          ],
        },
        {
          text: 'Internal Configuration',
          tooltip: 'Internal Configuration',
          icon: faSliders,
          path: `${envService.LEGACY_STUDIO_APP_URL}/#/settings/internal-configure/permissions`,
          requireHubPermissions: ['bf.badm'], //[AdminAccessTypes.BFAdmin],
        },
        {
          text: 'Demo Mode',
          tooltip: 'Demo Mode',
          icon: faPresentation,
          path: 'measure-studio/demo-config',
          requireHubPermissions: ['bf.badm'], //[AdminAccessTypes.BFAdmin],
        },
      ],
    ]);
  }
}
